import { Palette, RotateCcw, Trash2 } from 'lucide-react'
import { fmt } from '../format'
import { DEFAULT_GRADE, GRADE_LIMITS, type ColorGrade } from '../model'
import { InspectorPanel } from './InspectorPanel'
import styles from '../styles/inspector.module.scss'

export interface GradeInspectorProps {
  grade: ColorGrade
  onPatch: (patch: Partial<ColorGrade>) => void
  onDelete: () => void
}

/** The four controls, in the order the filter composes them — see
 * ``gradeFilter``. Reading the panel top to bottom is reading the chain. */
const CONTROLS = [
  { key: 'brightness', label: 'Brilho', step: 0.01, unit: '×' },
  { key: 'contrast', label: 'Contraste', step: 0.01, unit: '×' },
  { key: 'saturation', label: 'Saturação', step: 0.01, unit: '×' },
  { key: 'hue', label: 'Tonalidade', step: 1, unit: '°' },
] as const

/**
 * Colour correction for the selected range.
 *
 * Only what a CSS ``filter`` can preview lives here, and that is the whole
 * design: white point, black point, shadows and vignette have no filter
 * equivalent, so a control for them would show one thing on screen and
 * render another. The note at the bottom says so rather than leaving the
 * absence to be discovered.
 */
export function GradeInspector({ grade, onPatch, onDelete }: GradeInspectorProps) {
  return (
    <InspectorPanel
      icon={<Palette aria-hidden />}
      title="Coloração"
      meta={`${fmt(grade.start)} – ${fmt(grade.end)}`}
      actions={
        <button
          type="button"
          className={styles.inspectorDelete}
          onClick={onDelete}
          aria-label="Excluir coloração"
          title="Excluir coloração"
        >
          <Trash2 aria-hidden />
        </button>
      }
    >

      {CONTROLS.map((control) => {
        const limits = GRADE_LIMITS[control.key]
        const value = grade[control.key]
        const neutral = DEFAULT_GRADE[control.key]
        return (
          <div key={control.key} className={styles.field}>
            <span className={styles.fieldLabel}>{control.label}</span>
            <div className={styles.sliderRow}>
              <input
                type="range"
                className={styles.slider}
                min={limits.min}
                max={limits.max}
                step={control.step}
                value={value}
                onChange={(e) => {
                  onPatch({ [control.key]: Number(e.target.value) })
                }}
                aria-label={control.label}
              />
              <span className={styles.sliderValue}>
                {control.unit === '°'
                  ? `${String(Math.round(value))}°`
                  : `${value.toFixed(2).replace('.', ',')}×`}
              </span>
              <button
                type="button"
                className={styles.resetBtn}
                disabled={value === neutral}
                onClick={() => {
                  onPatch({ [control.key]: neutral })
                }}
                aria-label={`Voltar ${control.label.toLowerCase()} ao normal`}
                title="Voltar ao normal"
              >
                <RotateCcw aria-hidden />
              </button>
            </div>
          </div>
        )
      })}

      <p className={styles.hintSmall}>
        O que você vê é o que é renderizado: só entram ajustes que o navegador consegue
        pré-visualizar. Ponto branco, ponto preto, sombras e vinheta ficam de fora.
      </p>
    </InspectorPanel>
  )
}
